import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldProps, ScreenProperty } from '../../../../../../../@types/types'
import { CostsScreenValidationResult } from '../../../../../../../@types/validation-types'
import ToggleSwitch from '../../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import Deposit from '../deposit/Deposit'
import { parseNumber } from '../../../../../../../services/validation/rules/validationFunctions'

export interface DepositWithToggleProps extends FieldProps<CostsScreenValidationResult> {
  doReset: (clearingUpOf: ScreenProperty[]) => void
}

const elementId = 'deposit-with-toggle'

const DepositWithToggle: React.FC<DepositWithToggleProps> = ({
  insertionData,
  validationMessages,
  screenName,
  wrapperClassName = 'grid-item one-whole margin-top-l',
  onChange,
  doReset,
}) => {
  const { t } = useTranslation()

  // Check if deposit equals 3 times base rent
  const baseRent = insertionData.price ? parseNumber(insertionData.price) : 0
  const currentDeposit = insertionData.deposit ? parseNumber(insertionData.deposit) : 0
  const calculatedDeposit = baseRent * 3

  // Default to ON if no deposit is set or if deposit equals 3x base rent
  const isAutoCalculated = !insertionData.deposit || (currentDeposit === calculatedDeposit && baseRent > 0)
  const [isThreeMonthsDeposit, setIsThreeMonthsDeposit] = useState(isAutoCalculated)

  // Auto-calculate deposit when toggle is ON and base rent changes
  useEffect(() => {
    if (isThreeMonthsDeposit && baseRent > 0) {
      const screenProperty: ScreenProperty = {
        screen: screenName,
        property: 'deposit',
      }
      onChange(screenProperty, calculatedDeposit.toLocaleString('de-DE'))
    }
  }, [baseRent, isThreeMonthsDeposit, calculatedDeposit, screenName, onChange])

  // Keep toggle state in sync with deposit value
  useEffect(() => {
    if (baseRent > 0 && currentDeposit === calculatedDeposit) {
      setIsThreeMonthsDeposit(true)
    }
  }, [currentDeposit, calculatedDeposit, baseRent])

  // Handle toggle switch changes
  const doToggle = () => {
    const newValue = !isThreeMonthsDeposit
    setIsThreeMonthsDeposit(newValue)

    if (newValue && baseRent > 0) {
      // When switching to auto-calculation, set 3x base rent
      const screenProperty: ScreenProperty = {
        screen: screenName,
        property: 'deposit',
      }
      onChange(screenProperty, calculatedDeposit.toLocaleString('de-DE'))
    } else {
      // When switching to custom deposit, clear the field
      callReset()
    }
  }

  const callReset = () => {
    const resetForDeposit: ScreenProperty = {
      screen: screenName,
      property: 'deposit',
    }
    doReset([resetForDeposit])
  }

  return (
    <div className={wrapperClassName}>
      <div className='grid'>
        <div className='grid-item'>
          <ToggleSwitch
            id={elementId}
            alignment='maxSpread'
            propertyName='depositAutoCalculation'
            label={
              <span>{t('wizard.screen.costs.depositAutoCalculation', 'Die Kaution entspricht 3 Nettokaltmieten')}</span>
            }
            value={isThreeMonthsDeposit}
            onChange={doToggle}
          />
        </div>
        {!isThreeMonthsDeposit && (
          <div className='grid-item one-whole margin-top-m'>
            <Deposit
              insertionData={insertionData}
              validationMessages={validationMessages}
              screenName={screenName}
              wrapperClassName=''
              onChange={onChange}
              showLabelWithTooltip={true}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default DepositWithToggle
